import { getConfig, loadConfig } from "lib/config";
import { PublicClientApplication } from "@azure/msal-browser";

let msalInstance: PublicClientApplication | undefined;

export function getMsalConfig() {
  const config = getConfig();
  if (!config?.SSO) return undefined;

  const ssoConfig = config.SSO;
  return {
    auth: {
      clientId: ssoConfig.frontendClientId,
      authority: `https://login.microsoftonline.com/${ssoConfig.tenantId}`,
      redirectUri: ssoConfig.redirectUri,
      postLogoutRedirectUri: ssoConfig.redirectUri,
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: "localStorage",
      storeAuthStateInCookie: false,
    },
  };
}

const loginRequest = {
  scopes: ["openid", "profile", "email", "offline_access"],
};

function getLoginRequest() {
  const base = loginRequest;
  const appIdUri = getConfig().SSO?.appIdUri;
  if (appIdUri === undefined) {
    console.error("Could not push App ID URI to login request scopes");
    return base;
  }

  if (base.scopes.includes(appIdUri)) return base;

  base.scopes.push(appIdUri);
  return base;
}

export async function initMsal(): Promise<PublicClientApplication | undefined> {
  if (typeof window === "undefined") {
    console.info("Skipping config load during SSR");
    return;
  }

  await loadConfig();
  const msalConfig = getMsalConfig();
  if (!msalConfig) return undefined;

  msalInstance = new PublicClientApplication(msalConfig);
  await msalInstance.initialize();
  return msalInstance;
}

export function getMsalInstance() {
  return msalInstance;
}

export async function getMsalToken(): Promise<string | null> {
  const instance = getMsalInstance();
  if (!instance) {
    console.warn("No MSAL instance available");
    return null;
  }

  const account = instance.getActiveAccount();
  if (!account) {
    console.warn("No MSAL accounts available");
    return null;
  }

  try {
    const response = await instance.acquireTokenSilent({
      ...getLoginRequest(),
      account: account,
    });
    return response.accessToken;
  } catch (error) {
    console.error("Failed to acquire token silently", error);
    return null;
  }
}

export async function msalLogin(): Promise<boolean> {
  const instance = getMsalInstance();
  if (!instance) {
    console.warn("No MSAL Instance available");
    return false;
  }

  try {
    const resp = await instance.loginPopup(getLoginRequest());
    instance.setActiveAccount(resp.account);
    return true;
  } catch (error) {
    console.error("MSAL login failed", error);
    return false;
  }
}
